import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener('click', this.#handleAppstleClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener('click', this.#handleAppstleClick);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Event-delegated handler for the Appstle Loyalty redeem widget shown in the
   * cart drawer. Two jobs:
   *  1. Make the WHOLE widget banner clickable, not just its inner control
   *     ("Giriş Yap" when logged out, "Ödüller" when logged in). Appstle only
   *     wires the small inner link/button; a click on the surrounding banner is
   *     forwarded to that single control so the entire area behaves as a button.
   *  2. Close the cart drawer so Appstle's rewards modal is fully visible.
   * Delegation ensures this works regardless of when the app injects its DOM.
   * @param {Event} event
   */
  #handleAppstleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const block = target.closest('.appstle-loyalty-redeem-widget-block');
    if (!block) return;

    // If the click did not already land on an interactive control, forward it to
    // the widget's single control. Appstle marks that control with an <a>/<button>
    // or, failing that, cursor:pointer — so locate it generically.
    if (!target.closest('a[href], button, [role="button"]')) {
      const action =
        block.querySelector('a[href], button, [role="button"]') ||
        Array.from(block.querySelectorAll('*')).find(
          (el) => getComputedStyle(/** @type {Element} */ (el)).cursor === 'pointer'
        );

      if (action) {
        event.preventDefault();
        if (action instanceof HTMLAnchorElement && action.href) {
          window.location.href = action.href;
          return;
        }
        /** @type {HTMLElement} */ (action).click();
      }
    }

    this.closeDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });

  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

// Visual affordance: the Appstle redeem widget banner is fully clickable
// (see CartDrawerComponent#handleAppstleClick), so show a pointer over the
// whole banner — not only over its inner link/button.
if (!document.getElementById('wiwify-appstle-clickable-style')) {
  const style = document.createElement('style');
  style.id = 'wiwify-appstle-clickable-style';
  style.textContent =
    '.appstle-loyalty-redeem-widget-block,' +
    '.appstle-loyalty-redeem-widget-block .loyalty-cart-widget-button{cursor:pointer}' +
    '.appstle-loyalty-redeem-widget-block:hover{opacity:.85;transition:opacity .15s ease}';
  document.head.appendChild(style);
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
